import { v4 as uuidv4 } from 'uuid';

import {
  KidInterface,
  ListingSearchRoomInterface,
} from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import { FALLBACK_IMAGE_URL, FILTERS_QUERY_PARAMS } from '@constants/constants';
import { validRoomChildAge } from '@utils/dateUtil';
import {
  HotelDetailImageViewModel,
  HotelListPriceViewModel,
  HotelPromotionType,
} from '@api/index';
import { SliderImage } from '@components/ui/listing/ListingItemSlider';
import { Prices } from '@components/ui/cards/ListingCard/ListingCardInterface';

type FormatedRoomsProps = {
  title: string;
  adultCount: number;
  childrenList: FormatedChildProps[];
  error: boolean;
  key: string;
};

type FormatedChildProps = {
  age: Date | string;
  error?: boolean;
};

// =========================================================================
// This function is for converting the query params to hotel rooms
// =========================================================================
export const convertQueryRoomToHotelRooms = (room: string) => {
  const rooms: Array<ListingSearchRoomInterface> = [];
  if (!room) return rooms;

  if (room) {
    if (Array.isArray(room)) {
      room.forEach((item) => {
        const [adultCount, ...others] = item.split('_');
        const children: Array<KidInterface> = [];

        others?.map((item: string) =>
          children.push({
            id: uuidv4(),
            error: false,
            age: item?.replace(/-/g, '/')?.split('/')?.reverse()?.join('/'),
          })
        );
        rooms.push({
          id: uuidv4(),
          adultCount: Number(adultCount),
          childrenList: children,
          title: `${rooms.length + 1}. Oda`,
          closable: true,
          error: false,
          key: rooms.length > 0 ? String(rooms[rooms.length - 1].key + 1) : '1',
        });
      });
    } else {
      const [adultCount, ...others] = room.split('_');
      const children: Array<KidInterface> = [];
      others?.map((item) =>
        children.push({
          id: uuidv4(),
          error: false,
          age: item?.replace(/-/g, '/')?.split('/')?.reverse()?.join('/'),
        })
      );
      rooms.push({
        id: crypto.randomUUID(),
        adultCount: Number(adultCount),
        childrenList: children,
        title: `${rooms.length + 1}. Oda`,
        closable: true,
        error: false,
        key: rooms.length > 0 ? String(rooms[rooms.length - 1].key + 1) : '1',
      });
    }
  }

  return rooms;
};

export const clearQueryParamsFilters = (query) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page, ...nextQuery } = query;
  for (let i = 0; i < FILTERS_QUERY_PARAMS.length; i++) {
    delete nextQuery[FILTERS_QUERY_PARAMS[i]];
  }

  return { nextQuery };
};
// ==========================================
// Validate a children array of type KidInterface
// ==========================================
export const validateChildren = (children: KidInterface[]) => {
  let isValid = true;

  for (const item of children) {
    if (!validRoomChildAge(String(item.age))) isValid = false;
  }
  return isValid;
};

// ================================================
// Validate a room array of type ListingSearchRoomInterface
// ================================================
export const validateGuestsChildrenAges = (
  hotelRooms: ListingSearchRoomInterface[]
) => {
  if (!hotelRooms.length) return false;
  let isError = false;

  for (const item of hotelRooms) {
    if (!validateChildren(item.childrenList)) isError = true;
  }

  return isError;
};

// =========================================================================
// This function is for getting the discount type ( % | number)
// =========================================================================
export const getDiscountType = (type?: HotelPromotionType) => {
  return type === HotelPromotionType.PERCENT_DISCOUNT
    ? 'percent'
    : type === HotelPromotionType.VALUE_DISCOUNT
    ? 'value'
    : 'percent';
};

export const formatRooms = (hotelRooms: ListingSearchRoomInterface[]) => {
  const rooms: FormatedRoomsProps[] = [];

  hotelRooms?.forEach((room) => {
    rooms.push({
      title: room.title,

      adultCount: room.adultCount,

      childrenList: formatRoomChildren(room.childrenList),

      error: room.error,

      key: room.key,
    });
  });

  return rooms;
};

export const formatRoomChildren = (roomChildren: KidInterface[]) => {
  const children: FormatedChildProps[] = [];

  roomChildren?.forEach((child) => {
    children.push({
      age: child.age,

      error: child.error,
    });
  });

  return children;
};

export const getHotelMobileHeaderGuestsLabel = (roomQuery: string) => {
  if (!roomQuery) return '';

  const rooms = convertQueryRoomToHotelRooms(roomQuery);
  // ===================================
  // Format the values before display them
  // ===================================
  const adultNumber = rooms.reduce((t, a) => t + a.adultCount, 0);
  const childrenCount = rooms.reduce((t, a) => t + a.childrenList.length, 0);
  const roomGuestsLabel = `${rooms.length} Oda, ${adultNumber} Yetişkin   ${
    childrenCount > 0 ? ', ' + childrenCount + ' Çocuk' : ''
  }`;

  return roomGuestsLabel;
};

export const formatHotelPrices = (
  prices?: HotelListPriceViewModel
): Prices | undefined => {
  if (!prices) return undefined;
  return {
    original: prices.original,
    selling: prices.selling,
    discounted: prices.discounted,
    milePrice: prices.milePrice || 0,
    details: prices.details,
    currency: prices.currency,
    rate: prices.rate,
  };
};

export const formatHotelSliderImage = (
  images?: HotelDetailImageViewModel[]
): SliderImage[] =>
  images?.map((i) => ({
    imageUrl: i?.url || FALLBACK_IMAGE_URL,
    alt: i?.hotelImageCategoryName || '',
    providerId: i?.providerId,
    placeholderUrl: i?.placeholderUrl,
  })) || [];

/**
 * Build backend room array from query param.
 * Input examples:
 *  '1_2024-10-10_2025-10-10,2'  -> ['1,2024-10-10,2025-10-10','2']
 *  ['2_2024-10-10','1']        -> ['2,2024-10-10','1']
 * Rule:
 *  - Segment format: adultCount _ childDob _ childDob ...
 *  - Output per segment: adultCount if no children, else adultCount + all childDob values separated by commas
 */
export const buildBackendRoomArray = (
  roomParam: string | string[] | undefined
): string[] => {
  if (!roomParam) return ['2']; // Default is 2 adults if no param provided
  const roomString = Array.isArray(roomParam)
    ? roomParam.join(',')
    : String(roomParam);
  const segments = roomString.split(',');
  const result: string[] = [];
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (!segment) continue;
    const parts = segment.split('_').filter(Boolean);
    if (!parts.length) continue;
    const adultCount = parts[0];
    const childDates = parts.slice(1);
    if (childDates.length === 0) {
      result.push(adultCount);
    } else {
      result.push([adultCount, ...childDates].join(','));
    }
  }
  return result;
};
